import { MenuItemType } from './../domain/menu-item';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { createReducer, on } from '@ngrx/store';
import { MenuIconData, MenuItem } from '../domain/menu-item';
import { ProfileType } from './components/menu-editor-screen/menu-editor-screen.view-model';
import * as Actions from './menu-editor.actions';

export const featureKey = 'cxbMenuEditor';

export interface FeatureState {
  loading: boolean;
  modified: boolean;
  menuItems: MenuItem[];
  currentProfileType?: ProfileType;
  numberOfVisible: number;
  platforms: Record<string, string>;
  currentPlatformId?: string;
}

export interface AppState {
  [featureKey]: FeatureState;
}

export const initialState: FeatureState = {
  loading: false,
  modified: false,
  menuItems: [],
  platforms: {},
  currentPlatformId: undefined,
  currentProfileType: undefined,
  numberOfVisible: 0,
};

export const reducer = createReducer(
  initialState,
  on(
    Actions.loadCxbMenuEditorPage,
    (state, action): FeatureState => ({
      ...state,
      loading: true,
      menuItems: [],
      numberOfVisible: 0,
      modified: false,
      currentProfileType: action.profileType,
    })
  ),
  on(
    Actions.loadCxbMenuEditorPageFail,
    (state): FeatureState => ({ ...state, loading: false, menuItems: [], numberOfVisible: 0, modified: false })
  ),
  on(
    Actions.loadCxbMenuEditorPageSuccess,
    (state, action): FeatureState => ({
      ...state,
      // loading: false, TODO: this is a quick solution for loading, as the actions are chained, effects and actions needs refactor
      modified: false,
      platforms: action.platforms,
    })
  ),
  on(
    Actions.loadCxbMenuEditorPageWithPlatform,
    (state, action): FeatureState => ({
      ...state,
      currentPlatformId: action.platformId,
      currentProfileType: action.profileType,
      platforms: action.platforms,
    })
  ),
  on(
    Actions.loadCxbMenuEditorPageWithPlatformSuccess,
    (state, action): FeatureState => ({
      ...state,
      loading: false,
      menuItems: action.menuItems,
      numberOfVisible: action.menuItems.filter((i) => i.visible).length,
      modified: false,
    })
  ),
  on(
    Actions.loadCxbMenuEditorPageWithPlatformFail,
    (state): FeatureState => ({
      ...state,
      loading: false,
      menuItems: [],
      numberOfVisible: 0,
      modified: false,
    })
  ),
  on(
    Actions.changeCxbMenuItemsOrder,
    (state, action): FeatureState => ({
      ...state,
      menuItems: zipReorderedArrays(action.movement, state.menuItems),
      modified: true,
    })
  ),
  on(
    Actions.changeCxbMenuItemVisibility,
    (state, action): FeatureState => ({
      ...state,
      numberOfVisible: action.visible ? state.numberOfVisible + 1 : state.numberOfVisible - 1,
      menuItems: setVisibility(state.menuItems, state.numberOfVisible, action.pageId, action.visible),
      modified: true,
    })
  ),
  on(
    Actions.saveIconsCxbMenuEditorPage,
    (state, action): FeatureState => ({
      ...state,
      loading: false,
      modified: true,
      menuItems: saveIconsToMenuItem(state.menuItems, action.pageId, action.icons, action.profile),
    })
  ),
  on(
    Actions.changeCxbMenuItemType,
    (state, action): FeatureState => ({
      ...state,
      loading: false,
      modified: true,
      menuItems: setMenuItemType(state.menuItems, action.pageId, action.menuItemType),
    })
  )
);

const zipReorderedArrays = (movement: { from: number; to: number }, menuItems: MenuItem[]): MenuItem[] => {
  const clonedArray = menuItems.slice();
  moveItemInArray(clonedArray, movement.from, movement.to);
  return reorderItems(clonedArray);
};

const setVisibility = (menuItems: MenuItem[], numberOfVisible: number, pageId: string, visible: boolean): MenuItem[] => {
  const clonedArray = menuItems.slice();
  const itemIndex = clonedArray.findIndex((i) => i.pageId === pageId);
  const resultIndex = visible ? numberOfVisible : numberOfVisible - 1;
  clonedArray.splice(itemIndex, 1, { ...clonedArray[itemIndex], visible });
  moveItemInArray(clonedArray, itemIndex, resultIndex);
  return reorderItems(clonedArray);
};

const setMenuItemType = (menuItems: MenuItem[], pageId: string, menuItemType: MenuItemType): MenuItem[] => {
  const clonedArray = menuItems.slice();
  return clonedArray.map((menuItem) => {
    if (menuItem.pageId === pageId) {
      return {
        ...menuItem,
        menuItemType,
      };
    }

    return menuItem;
  });
};

const reorderItems = (menuItems: MenuItem[]): MenuItem[] => {
  return menuItems.map((i, index) => ({
    ...i,
    order: i.visible ? index + 1 : undefined,
  }));
};

const saveIconsToMenuItem = (
  menuItems: MenuItem[],
  pageId: string,
  icons: { focused: MenuIconData; normal: MenuIconData; highlighted: MenuIconData; selected: MenuIconData },
  profile: string
): MenuItem[] => {
  const clonedArray = menuItems.slice();
  const itemIndex = clonedArray.findIndex((i) => i.pageId === pageId && i.profileType === profile);
  clonedArray.splice(itemIndex, 1, {
    ...clonedArray[itemIndex],
    focusedIcon: icons.focused,
    normalIcon: icons.normal,
    highlightedIcon: icons.highlighted,
    selectedIcon: icons.selected,
  });
  return clonedArray;
};
